import { lazy, Suspense, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { LogOut, User, Settings, Baby, ArrowLeft, Search, Bookmark } from 'lucide-react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useAppSignOut } from '@/shared/hooks/useAppSignOut'
import { AudioProvider, useAudio } from '@/features/content/AudioContext'
import { ChatNavIcon } from '@/features/chat'
import { MiniAudioPlayer } from '@/features/content/MiniAudioPlayer'

import { ShortChapterContext } from '@/features/content/ShortChapterContext'
import { isOneOfPaths, isUnderPath, listRouteFor } from '@/shared/lib/routeMatch'
/**
 * Nachgeladen statt mitgeliefert: Das Overlay zieht ueber die Leseansicht die ganze
 * Markdown-Kette samt KaTeX hinter sich her - gut 410 kB, die beim Start der App
 * niemand braucht. Es erscheint erst, wenn ein Kurzkapitel geoeffnet wird.
 *
 * Der Adapter ist noetig, weil es eine benannte Komponente ist und keine Seite; der
 * Coding Style gibt Default-Exporte nur den Seiten.
 */
const ShortChapterOverlay = lazy(() =>
  import('@/features/content/ShortChapterOverlay').then((m) => ({
    default: m.ShortChapterOverlay,
  }))
)

const base = '/user'

function LayoutInner() {
  const navigate = useNavigate()
  const location = useLocation()
  const signOut = useAppSignOut()
  const { isReadingOverlayOpen } = useAudio()
  const [shortChapterSlug, setShortChapterSlug] = useState<string | null>(null)

  const { pathname } = location
  const isMainPage = isOneOfPaths(pathname, [
    base,
    `${base}/tracking`,
    `${base}/entdecken`,
    `${base}/meine-titel`,
    `${base}/chat`,
  ])
  const isSubRoute = !isMainPage
  // Im geöffneten Chat gibt es kein Bottom-Menü: Die Eingabe sitzt unten, und die
  // Tastatur schiebt sich darunter, wie bei WhatsApp. Zurück geht es über den Pfeil.
  const isChatThread = isUnderPath(pathname, `${base}/chat`)
  // Diese Listen haben keinen Kopf mit Logo und Profil-Knopf: Ins Profil geht es über
  // "Begleitung", und der Inhalt beginnt direkt unter der Statusleiste.
  const isHeaderless = isOneOfPaths(pathname, [
    `${base}/entdecken`,
    `${base}/meine-titel`,
    `${base}/chat`,
  ])
  const isProfilePage = isOneOfPaths(pathname, [`${base}/profile`])
  const isTrackingSubPage = isUnderPath(pathname, `${base}/tracking`)
  const isChapterPage = isUnderPath(pathname, `${base}/chapter`)
  const isArticlePage = isChapterPage || isUnderPath(pathname, `${base}/articles`)
  const showProfileButton = isMainPage || isProfilePage || isTrackingSubPage || isArticlePage

  // Wohin der Zurück-Pfeil führt: Kapitel merken sich ihre Herkunft im `from`-Parameter.
  const getParentRoute = () => {
    const from = new URLSearchParams(location.search).get('from')
    const fromList = isChapterPage ? listRouteFor(from) : null
    if (fromList) return fromList
    if (isTrackingSubPage) return `${base}/tracking`
    if (isChatThread) return `${base}/chat`
    return base
  }

  return (
    <ShortChapterContext.Provider value={{ openShortChapter: setShortChapterSlug }}>
      <Suspense fallback={null}>
        <ShortChapterOverlay slug={shortChapterSlug} onClose={() => setShortChapterSlug(null)} />
      </Suspense>
      <div className="flex flex-col w-full h-screen bg-background">
        <header
          className={`fixed top-0 left-0 right-0 z-30 mt-safe flex items-center justify-between w-full px-4 h-14 bg-transparent pointer-events-none ${isHeaderless ? 'hidden' : ''}`}
        >
          {isReadingOverlayOpen ? (
            <div className="w-10 h-10" />
          ) : isSubRoute ? (
            <button
              onClick={() => navigate(getParentRoute())}
              className="pointer-events-auto flex items-center justify-center w-10 h-10 rounded-full bg-white/50 dark:bg-card/80 backdrop-blur-md text-primary dark:text-[hsl(10,55%,81%)] hover:opacity-70 transition-opacity"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2} />
            </button>
          ) : (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/50 dark:bg-card/80 backdrop-blur-md overflow-hidden">
              <div
                role="img"
                aria-label="Logo"
                className="bg-primary dark:bg-[hsl(10,55%,81%)]"
                style={{
                  height: '2rem',
                  aspectRatio: '647 / 721',
                  WebkitMaskImage: 'url(/ifs_transparent.png)',
                  maskImage: 'url(/ifs_transparent.png)',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
              />
            </div>
          )}
          {showProfileButton ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="pointer-events-auto flex items-center justify-center w-10 h-10 rounded-full bg-white/50 dark:bg-card/80 backdrop-blur-md text-primary dark:text-[hsl(10,55%,81%)] hover:opacity-70 focus:outline-none transition-opacity">
                  <Settings className="w-6 h-6" strokeWidth={1.2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 sm:w-48 bg-popover">
                <DropdownMenuItem
                  onClick={() => navigate(`${base}/profile`)}
                  className="py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm"
                >
                  <User className="w-5 h-5 sm:w-4 sm:h-4 mr-3 sm:mr-2" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await signOut()
                    navigate('/')
                  }}
                  className="py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm"
                >
                  <LogOut className="w-5 h-5 sm:w-4 sm:h-4 mr-3 sm:mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="w-8" />
          )}
        </header>
        <main
          id="main-scroll"
          className="fixed inset-0 w-full px-0 pb-20 overflow-y-auto overflow-x-hidden bg-background"
        >
          <div className={isHeaderless ? 'pt-safe' : 'h-safe-14'} />
          <Outlet />
        </main>
        {/* Im Chat läge der schwebende Player genau über der Eingabezeile. Das Audio läuft
            im AudioProvider weiter, nur die Karte ist dort nicht zu sehen. */}
        {!isChatThread && <MiniAudioPlayer />}
        {!isChatThread && (
          <nav className="fixed bottom-0 left-0 right-0 z-50 flex flex-row items-start justify-around h-20 sm:h-16 pt-2 sm:pt-0 pb-safe sm:items-center bg-card border-t border-border">
            <NavLink
              to={base}
              end
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 ${isActive || location.pathname.startsWith(`${base}/tracking`) || location.pathname.startsWith(`${base}/chapter`) || location.pathname.startsWith(`${base}/articles`) ? 'text-primary dark:text-white' : 'text-primary/40 dark:text-accent'}`
              }
            >
              <Baby className="w-6 h-6 sm:w-5 sm:h-5" />
              <span className="text-xs">Begleitung</span>
            </NavLink>

            <NavLink
              to={`${base}/entdecken`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 ${isActive ? 'text-primary dark:text-white' : 'text-primary/40 dark:text-accent'}`
              }
            >
              <Search className="w-6 h-6 sm:w-5 sm:h-5" />
              <span className="text-xs">Entdecken</span>
            </NavLink>

            <NavLink
              to={`${base}/meine-titel`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 ${isActive ? 'text-primary dark:text-white' : 'text-primary/40 dark:text-accent'}`
              }
            >
              <Bookmark className="w-6 h-6 sm:w-5 sm:h-5" />
              <span className="text-xs">Meine Titel</span>
            </NavLink>

            <NavLink
              to={`${base}/chat`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 px-3 ${isActive ? 'text-primary dark:text-white' : 'text-primary/40 dark:text-accent'}`
              }
            >
              <ChatNavIcon viewer="woman" className="w-6 h-6 sm:w-5 sm:h-5" />
              <span className="text-xs">Chats</span>
            </NavLink>
          </nav>
        )}
      </div>
    </ShortChapterContext.Provider>
  )
}

export default function UserLayout() {
  return (
    <AudioProvider>
      <LayoutInner />
    </AudioProvider>
  )
}
